import React from 'react';
import { InspectModeCard } from '../InspectModeCard';
import { Box, Typography, Flex, Divider, Pill } from '@fullstory/components';
import { BehaviorSignalType } from '@fullstory/proto/fs/services/ingestion-settings';
import { useSelector } from 'react-redux';
import { ingestionSettingsSelectors, watchedElementsSelectors } from '@fullstory/redux';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import css from './index.module.scss';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { FGACButton } from '@fullstory/library-ui';
import { NamedElementRecordMatch } from '@fullstory/named-elements';
import { platformToLabel } from '@fullstory/util/pages';

const { getWatchedNamedElementIds } = watchedElementsSelectors;
const { getNamedElementBlockedSignals } = ingestionSettingsSelectors;

type ElementCardProps = {
  onClick: (isNew: boolean) => void;
  namedElementRecords: NamedElementRecordMatch[];
};

const NewElementContent = () => {
  const color = 'neutrals-n10';
  const variant = 'body2';

  return (
    <Flex
      data-component="NewElementContent"
      column
      alignItems="flex-start"
      gap={8}
      testId="element-card-inspect-mode-new-content"
    >
      <Typography variant={variant}>
        <FormattedMessage defaultMessage="Creating an Element allows you to:" id="iUareQ" />
      </Typography>
      <ul className={css.bulletedList}>
        <li>
          <Typography variant={variant} color={color}>
            <FormattedMessage defaultMessage="Watch Element" id="7fvH5B" />
          </Typography>
        </li>
        <li>
          <Typography variant={variant} color={color}>
            <FormattedMessage defaultMessage="Ignore frustration signals" id="2T+txn" />
          </Typography>
        </li>
      </ul>
    </Flex>
  );
};

const DisplayList = ({ items }: { items: string[] }) => {
  if (items.length == 0)
    return (
      <Typography color="neutrals-n6" variant="body2">
        <FormattedMessage defaultMessage="None selected" id="7MYtB4" />
      </Typography>
    );
  return (
    <Typography data-component="DisplayList" variant="body2">
      {items.join(', ')}
    </Typography>
  );
};

const blockedSignalToLabel: Map<BehaviorSignalType, string> = new Map([
  [
    BehaviorSignalType.SIGNAL_RAGE_CLICK,
    intl.formatMessage({ defaultMessage: 'Rage click', id: 'CUgRTU' }),
  ],
  [
    BehaviorSignalType.SIGNAL_DEAD_CLICK,
    intl.formatMessage({ defaultMessage: 'Dead click', id: 'pOIWaa' }),
  ],
  [
    BehaviorSignalType.SIGNAL_INVALID,
    intl.formatMessage({ defaultMessage: 'Invalid', id: '89T/ze' }),
  ],
]);

type ExistingElementContentProps = Pick<ElementCardProps, 'namedElementRecords'>;

const ExistingElementContent = ({ namedElementRecords }: ExistingElementContentProps) => {
  const watchedNamedElementIds = useSelector(getWatchedNamedElementIds);
  const namedElementBlockedSignals = useSelector(getNamedElementBlockedSignals);

  const namedElementId = namedElementRecords[0]?.namedElement?.id ?? ''; // can be empty if not created yet
  const watched = watchedNamedElementIds.has(namedElementId);
  const blockedSignals = namedElementBlockedSignals.get(namedElementId) ?? [];

  const platformSelectors = namedElementRecords[0]?.namedElement?.platformSelectors ?? [];
  const platformSelectorsNormalized = [...new Set(platformSelectors.map(p => p.platforms).flat())];

  const variant = 'body2';
  const color = 'neutrals-n7';
  const mainElement = namedElementRecords[0]?.namedElement!;

  return (
    <Flex
      data-component="ExistingElementContent"
      column
      alignItems="stretch"
      gap={12}
      testId="element-card-inspect-mode-existing-content"
    >
      <Box marginBottom={20}>
        <Pill key={mainElement.id} variant="primary">
          <Typography variant="body2" fontWeight="semibold">
            {mainElement.name}
          </Typography>
        </Pill>
      </Box>
      <Flex justifyContent="space-between">
        <Typography variant={variant} color={color}>
          <FormattedMessage defaultMessage="Platform" id="Mmuj1R" />
        </Typography>
        <DisplayList
          items={platformSelectorsNormalized.map(platform => platformToLabel[platform])}
        />
      </Flex>
      <Divider />
      <Flex justifyContent="space-between">
        <Typography variant={variant} color={color}>
          <FormattedMessage defaultMessage="Watched Element" id="Xt+LqS" />
        </Typography>
        <Typography variant={variant}>
          {watched
            ? intl.formatMessage({ defaultMessage: 'Yes', id: 'a5msuh' })
            : intl.formatMessage({ defaultMessage: 'No', id: 'oUWADl' })}
        </Typography>
      </Flex>
      <Divider />
      <Flex justifyContent="flex-start">
        <Typography variant={variant} color={color}>
          <FormattedMessage defaultMessage="Ignored frustration signals" id="PbZWkh" />
        </Typography>
      </Flex>
      <DisplayList items={blockedSignals.map(bs => blockedSignalToLabel.get(bs)!)} />
    </Flex>
  );
};

export const ElementCard = ({ onClick, namedElementRecords }: ElementCardProps) => {
  const flags = useFlags();
  const canModifyElements = flags.hasBit(Flag.PermModifyNamedElements);

  const exactMatchNamedElement = namedElementRecords.find(ne => ne.fullDepthMatch);
  // we may match on a more generic parent element, but we don't want to prevent users from naming
  // a more specific element, so just consider exact match
  const isNew = !exactMatchNamedElement;

  const buttonTitle = isNew
    ? intl.formatMessage({ defaultMessage: 'Create Element', id: 'TWGQeP' })
    : canModifyElements
    ? intl.formatMessage({ defaultMessage: 'Edit Element', id: 'NMcgWE' })
    : intl.formatMessage({ defaultMessage: 'View Element', id: 'LkELXm' });

  return (
    <InspectModeCard
      data-component="ElementCard"
      title={intl.formatMessage({ defaultMessage: 'Element', id: 'SHkKGm' })}
      control={
        <FGACButton
          onClick={() => onClick(isNew)}
          variant="outline"
          size="x-small"
          iconOnly={false}
          disabled={isNew && !canModifyElements}
          tooltip={intl.formatMessage({
            defaultMessage: 'Your account does not have permission to create elements.',
            id: '/Znt/r',
          })}
        >
          {buttonTitle}
        </FGACButton>
      }
    >
      {exactMatchNamedElement ? (
        <ExistingElementContent namedElementRecords={[exactMatchNamedElement]} />
      ) : (
        // we currently only show the single exact match element, but the component API
        // takes an array for potentially showing parent elements in the future
        <NewElementContent />
      )}
    </InspectModeCard>
  );
};
